import { Stethoscope, Phone, AlertTriangle, FileSearch, Scale, ClipboardCheck } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const MedicalMalpractice = () => {
  return (
    <GenericPracticePage
      title="Medical Malpractice Attorney"
      subtitle="When medical professionals fail to meet the standard of care, patients suffer devastating consequences. We hold negligent doctors, hospitals, and healthcare providers accountable for medical errors."
      icon={<Stethoscope className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />}
      mainContent={
        <div className="space-y-8">
          <section>
            <h2 className="text-3xl font-bold mb-4">Houston Medical Malpractice Lawyer</h2>
            <p className="text-lg text-muted-foreground mb-4">
              We trust medical professionals with our lives and health. When that trust is violated through negligence, the results can be catastrophic - permanent disability, worsening medical conditions, or even death. Medical malpractice cases are complex, requiring extensive medical knowledge and significant resources to prosecute.
            </p>
            <p className="text-lg text-muted-foreground">
              Our firm has the experience, medical experts, and determination to take on hospitals and insurance companies to get you the justice and compensation you deserve.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">What Is Medical Malpractice?</h2>
            <div className="bg-gradient-subtle p-6 rounded-lg">
              <p className="text-muted-foreground mb-4">
                Medical malpractice occurs when a healthcare provider deviates from the accepted standard of care, causing injury or death to a patient. To prove malpractice, we must establish:
              </p>
              <div className="space-y-3">
                <div className="flex gap-3 items-start">
                  <Scale className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Duty of Care</h4>
                    <p className="text-sm text-muted-foreground">A doctor-patient relationship existed</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <AlertTriangle className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Breach of Standard</h4>
                    <p className="text-sm text-muted-foreground">The provider failed to meet accepted medical standards</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <FileSearch className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Causation</h4>
                    <p className="text-sm text-muted-foreground">The breach directly caused your injury</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <ClipboardCheck className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Damages</h4>
                    <p className="text-sm text-muted-foreground">You suffered harm as a result</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Types of Medical Malpractice We Handle</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <h3 className="font-bold mb-2">Surgical Errors</h3>
                <p className="text-sm text-muted-foreground">Wrong-site surgery, retained instruments, anesthesia errors, unnecessary procedures</p>
              </div>
              <div className="p-4 border rounded-lg">
                <h3 className="font-bold mb-2">Misdiagnosis/Delayed Diagnosis</h3>
                <p className="text-sm text-muted-foreground">Failure to diagnose cancer, heart attack, stroke, or other serious conditions</p>
              </div>
              <div className="p-4 border rounded-lg">
                <h3 className="font-bold mb-2">Birth Injuries</h3>
                <p className="text-sm text-muted-foreground">Cerebral palsy, Erb's palsy, brain damage from oxygen deprivation</p>
              </div>
              <div className="p-4 border rounded-lg">
                <h3 className="font-bold mb-2">Medication Errors</h3>
                <p className="text-sm text-muted-foreground">Wrong medication, incorrect dosage, dangerous drug interactions</p>
              </div>
              <div className="p-4 border rounded-lg">
                <h3 className="font-bold mb-2">Hospital Negligence</h3>
                <p className="text-sm text-muted-foreground">Infections, bedsores, falls, inadequate staffing or supervision</p>
              </div>
              <div className="p-4 border rounded-lg">
                <h3 className="font-bold mb-2">Emergency Room Errors</h3>
                <p className="text-sm text-muted-foreground">Failure to properly triage or treat emergency conditions</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Common Consequences of Medical Negligence</h2>
            <div className="bg-primary/5 border border-primary/20 p-6 rounded-lg space-y-3">
              <p className="text-muted-foreground">• <strong>Permanent Disability:</strong> Paralysis, brain damage, loss of organ function</p>
              <p className="text-muted-foreground">• <strong>Worsened Medical Condition:</strong> Delayed diagnosis allowing disease progression</p>
              <p className="text-muted-foreground">• <strong>Additional Medical Expenses:</strong> Corrective surgeries and ongoing treatment</p>
              <p className="text-muted-foreground">• <strong>Loss of Quality of Life:</strong> Chronic pain, inability to work or enjoy activities</p>
              <p className="text-muted-foreground">• <strong>Wrongful Death:</strong> Loss of a loved one due to preventable medical error</p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Why Medical Malpractice Cases Are Complex</h2>
            <div className="space-y-4">
              <div className="border-l-4 border-accent pl-4">
                <h3 className="font-bold mb-2">Expert Testimony Required</h3>
                <p className="text-sm text-muted-foreground">Texas law requires medical experts to establish the standard of care and how it was breached. We work with top medical professionals in relevant specialties.</p>
              </div>
              <div className="border-l-4 border-accent pl-4">
                <h3 className="font-bold mb-2">Damage Caps</h3>
                <p className="text-sm text-muted-foreground">Texas limits non-economic damages in medical malpractice cases, making it crucial to maximize economic damages and identify all liable parties.</p>
              </div>
              <div className="border-l-4 border-accent pl-4">
                <h3 className="font-bold mb-2">Notice Requirements</h3>
                <p className="text-sm text-muted-foreground">Special pre-suit notice must be given, and expert reports must be filed within strict deadlines.</p>
              </div>
              <div className="border-l-4 border-accent pl-4">
                <h3 className="font-bold mb-2">Aggressive Defense</h3>
                <p className="text-sm text-muted-foreground">Hospitals and doctors have powerful insurance companies and legal teams defending them. You need experienced attorneys who aren't intimidated.</p>
              </div>
            </div>
          </section>

          <section className="bg-accent/5 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">How Long Do You Have to File?</h2>
            <div className="space-y-3">
              <p className="text-muted-foreground">
                <strong>Texas has a 2-year statute of limitations</strong> for medical malpractice claims from the date of the injury or when you discovered (or reasonably should have discovered) the injury.
              </p>
              <p className="text-muted-foreground">
                There's also a <strong>10-year statute of repose</strong> - no claim can be filed more than 10 years after the negligent act, regardless of when you discovered it.
              </p>
              <p className="text-muted-foreground font-bold text-accent">
                Don't wait. Medical malpractice cases require extensive investigation and preparation. Contact us immediately.
              </p>
            </div>
          </section>

          <section className="text-center bg-gradient-subtle p-8 rounded-lg">
            <Phone className="h-12 w-12 mx-auto mb-4 text-accent" />
            <h2 className="text-2xl font-bold mb-4">Victim of Medical Negligence?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              We provide compassionate representation for patients harmed by medical errors. Confidential consultation to review your case. No fee unless we win.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8329303059" className="text-2xl font-bold text-accent hover:text-accent/80">
                (832) 930-3059
              </a>
            </div>
          </section>
        </div>
      }
    />
  );
};

export default MedicalMalpractice;
